import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { ContractComparisonModel } from '../../models';
import { InstanceUtils, getVisibleComparisonGroups } from '../../misc';
import {
    BasicComparisonRenderer,
    LayersComparisonRenderer,
    SectionMatchingPanel,
    SectionsComparisonRenderer
} from '..';
import { useAppStore } from '../../stores';
import './ContractComparisonDetailsRenderer.less';

interface Props {
    contractComparison: ContractComparisonModel;
    isVisible: boolean;
}

const ContractComparisonDetailsRenderer: React.FC<Props> = ({ contractComparison, isVisible }) => {
    const { contractComparisonStore } = useAppStore();

    const matching = contractComparison.sectionMatching;

    const getClassName = () => {
        let className = 'contract-comparison-details-renderer';

        if (!isVisible) {
            className += ' hidden';
        }

        if (contractComparison.isExecutingAction) {
            className += ' loading';
        }

        return className;
    };

    const visibleComparisonNodes = contractComparison.comparisonNodes.filter(
        comparisonNode =>
            getVisibleComparisonGroups(comparisonNode.groups, contractComparisonStore.selectedFilter).length > 0
    );

    const showNoFieldsMessage = contractComparisonStore.selectedFilter !== 'all' && !visibleComparisonNodes.length;

    return (
        <div className={getClassName()}>
            {matching && <SectionMatchingPanel matching={matching} />}

            {/* Suggested matches need confirmation before any field comparisons are shown. */}
            {(!matching || matching.isConfirmed) && (
                <>
                    {showNoFieldsMessage && (
                        <div className="contract-comparison-details-empty-state">
                            <span>There are no fields matching the current filter.</span>
                        </div>
                    )}

                    {!showNoFieldsMessage &&
                        visibleComparisonNodes.map(comparisonNode => {
                            if (InstanceUtils.isBasicComparisonNode(comparisonNode)) {
                                return (
                                    <BasicComparisonRenderer
                                        key={comparisonNode.nodeId}
                                        comparisonNode={comparisonNode}
                                    />
                                );
                            }

                            if (InstanceUtils.isLayersComparisonNode(comparisonNode)) {
                                return (
                                    <LayersComparisonRenderer
                                        key={comparisonNode.nodeId}
                                        comparisonNode={comparisonNode}
                                    />
                                );
                            }

                            if (InstanceUtils.isSectionsComparisonNode(comparisonNode) && matching) {
                                return (
                                    <SectionsComparisonRenderer
                                        key={comparisonNode.nodeId}
                                        comparisonNode={comparisonNode}
                                    />
                                );
                            }

                            return null;
                        })}
                </>
            )}
        </div>
    );
};

export default observer(ContractComparisonDetailsRenderer);
